/**
 * @group Utils
 * @summary <blockquote>Error handling utilities for UI components.</blockquote>
 */

import { useToast } from '../hooks/useToast';
import { getErrorMessage } from './getErrorMessage';

/**
 * @summary <blockquote>Hook for displaying error toasts with appropriate messages.</blockquote>
 * 
 * @example
 * ```tsx
 * const { showErrorToast } = useErrorHandling();
 * 
 * try {
 *   await someOperation();
 * } catch (error) {
 *   showErrorToast(error);
 * }
 * ```
 */
export function useErrorHandling() {
  const { toast } = useToast();
  
  const showErrorToast = (
    error: Error | { statusCode?: number; errorMessage?: string } | unknown,
    title: string = 'Error'
  ) => {
    let statusCode: number | undefined;
    let errorMessage: string | undefined;
    
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'object' && error !== null) {
      statusCode = (error as any).statusCode;
      errorMessage = (error as any).errorMessage;
    }
    
    const message = getErrorMessage(statusCode, errorMessage);
    
    toast({
      title,
      description: message,
      variant: 'destructive',
    });
  };
  
  const showContextError = (componentName: string) => {
    toast({
      title: 'Configuration Error',
      description: `${componentName} must be used within the correct context. Please refresh the page.`,
      variant: 'destructive',
    });
  };
  
  return {
    showErrorToast,
    showContextError,
    getErrorMessage,
  };
}
